/**
 * STAND-IN for Maritime `IconButton` — not in @syia-ai/maritimeai-ui 0.2.0.
 * Delete this file and import it from Maritime once it ships.
 *
 * An icon-only button. `label` is required: it is the accessible name and the tooltip, so an
 * icon button can never be unnamed. Styled from Maritime theme variables (shell.css → Stand-ins).
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Tooltip } from '@syia-ai/maritimeai-ui';

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label' | 'children'> {
  /** Accessible name and tooltip text. */
  label: string;
  icon: ReactNode;
  /** `inverse` sits on a dark or brand surface. */
  tone?: 'default' | 'inverse';
  /** Extra content drawn over the icon, e.g. a CountBadge. */
  children?: ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, tone = 'default', className, children, type = 'button', ...rest },
  ref,
) {
  return (
    <Tooltip content={label} placement="bottom">
      <button
        ref={ref}
        type={type}
        aria-label={label}
        className={['shell-icon-button', `shell-icon-button--${tone}`, className].filter(Boolean).join(' ')}
        {...rest}
      >
        <span className="shell-icon-button__icon" aria-hidden>
          {icon}
        </span>
        {children}
      </button>
    </Tooltip>
  );
});

/** The same, for an in-app navigation: a router Link, so middle-click and new-tab work. */
export function IconLink({ to, label, icon }: { to: string; label: string; icon: ReactNode }) {
  return (
    <Tooltip content={label} placement="bottom">
      <Link to={to} aria-label={label} className="shell-icon-button shell-icon-button--default">
        <span className="shell-icon-button__icon" aria-hidden>
          {icon}
        </span>
      </Link>
    </Tooltip>
  );
}
